import React, { useContext, useState, useEffect } from 'react';
import classNames from 'classnames';
import Header from './components/Common/Header';
import Footer from './components/Common/Footer';
import NewSidebar from 'components/Common/NewSidebar';
import Routes from './pages/Routes';
import { ScrollToTop } from 'utils/ScrollToTop';
import { AppActionLogger } from 'utils/AppActionLogger';
import { GlobalContext } from './providers/GlobalProvider';
import { applyTheme } from 'utils/theme';
import ProductFruitsWrapper from 'utils/ProductFruitsWrapper';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const theme = createTheme({
  components: {
    MuiDialog: {
      styleOverrides: {
        root: {
          color: 'var(--text-primary-color)',
          fontFamily: 'Inter',
          '&.Mui-active': {
            color: 'var(--action-color)',
          },
          ' .Mui-completed': {
            color: 'var(--action-color)',
            fill: 'var(--action-color)',
          },
          ' .Mui-error': {
            borderColor: 'inherit',
          },
        },
        container: {
          ' .MuiButton-root': {
            backgroundColor: 'var(--Primary-400, var(--action-color))',
            color: 'white',
          },
          ' .MuiButton-root:hover': {
            backgroundColor: 'var(--Primary-400, var(--action-color))',
            color: 'white',
            border: 'inherit',
          },
          ' .MuiTypography-root': {
            color: 'var(--text-primary-color)',
            fontFamily: 'Inter',
            backgroundColor: 'var(--bg-primary)',
            borderRadius: '8px',
          },
          ' .MuiPaper-root': {
            color: 'var(--text-primary-color)',
            fontFamily: 'Inter',
            backgroundColor: 'inherit',
          },
          ' .MuiChip-root': {
            color: 'var(--text-primary-color)',
            fontFamily: 'Inter',
            backgroundColor: 'inherit',
          },
          ' .MuiSvgIcon-root': {
            color: 'inherit',
            fontFamily: 'Inter',
            '&.Mui-active': {
              color: 'var(--action-color)',
            },
          },
          ' .MuiSvgIcon-text': {
            color: 'inherit',
            fontFamily: 'Inter',
            backgroundColor: 'inherit',
          },
          ' .MuiFormControl-root': {
            color: 'var(--text-primary-color)',
            fontFamily: 'Inter',
            backgroundColor: 'inherit',
          },
          ' .Mui-completed': {
            color: 'var(--action-color)',
            fill: 'var(--action-color)',
          },
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          color: 'var(--text-primary-color)',
          backgroundColor: 'var(--bg-primary)',
          fontFamily: 'Inter',
          '&.Mui-active': {
            color: 'var(--action-color)',
          },
        },
      },
    },

    MuiTypography: {
      styleOverrides: {
        root: {
          color: 'var(--text-primary-color)',
          fontFamily: 'Inter',
        },
        caption: {
          color: 'var(--text-primary-color)',
          fontFamily: 'Inter',
        },
        body1: {
          color: 'var(--text-primary-color)',
          fontFamily: 'Inter',
        },
        body2: {
          color: 'var(--text-primary-color)',
          fontFamily: 'Inter',
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          color: ' var(--Text-Main, var(--text-primary))',
          // backgroundColor: 'var(--Background-200, var(--bg-colors))',
          fontFamily: 'Inter',
          '&.Mui-error': {
            borderColor:
              'var(--Colors-Border-border-primary, var(--bg-border-db))',
          },
        },
        root: {
          color: ' var(--Text-Main, var(--text-primary))',
          // backgroundColor: 'var(--Background-200, var(--bg-colors))',
          fontFamily: 'Inter',
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor:
              'var(--Colors-Border-border-primary, var(--bg-border-db))',
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        input: {
          color: ' var(--Text-Main, var(--text-primary))',
          // backgroundColor: 'var(--Background-200, var(--bg-colors))',
          fontFamily: 'Inter',
          '&.Mui-error': {
            borderColor:
              'var(--Colors-Border-border-primary, var(--bg-border-db))',
          },
          '&.MuiOutlinedInput-notchedOutline.Mui-error': {
            borderColor:
              'var(--Colors-Border-border-primary, var(--bg-border-db)) !important',
          },
        },
        root: {
          color: ' var(--Text-Main, var(--text-primary))',
          // backgroundColor: 'var(--Background-200, var(--bg-colors))',
          fontFamily: 'Inter',
          '&.Mui-error': {
            borderColor:
              'var(--Colors-Border-border-primary, var(--bg-border-db))',
          },
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: 'var(--text-primary-color)',
          fontFamily: 'Inter',
          width: 'fit-content',
        },
      },
    },

    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: 'var(--text-primary-color)',
          fontFamily: 'Inter',
          '&.Mui-active': {
            color: 'var(--action-color)',
          },
          '&.Mui-completed': {
            color: 'var(--action-color)',
            fill: 'var(--action-color)',
          },
        },
      },
    },

    MuiStepIcon: {
      styleOverrides: {
        text: {
          color: 'var(--bg-colors)',
          fontFamily: 'Inter',
          '&.Mui-active': {
            color: 'var(--action-color)',
          },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: 'Inter',
          border: 'none',
          padding: '0.5rem 1rem',
          borderRadius: '6px',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: '500',
          cursor: 'pointer',
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          color: 'var(--text-primary-color)',
          backgroundColor: 'var(--bg-primary)',
          fontFamily: 'Inter',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgb(255, 255, 255)',
          color: 'rgba(0, 0, 0, 0.87)',
          boxShadow:
            'rgba(0, 0, 0, 0.2) 0px 5px 5px -3px, rgba(0, 0, 0, 0.14) 0px 8px 10px 1px, rgba(0, 0, 0, 0.12) 0px 3px 14px 2px',
          transformOrigin: 'center top',
          transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          borderRadius: '4px',
          outline: '0px',
        },
      },
    },

    MuiTable: {
      styleOverrides: {
        root: {
          color: 'inherit',
          backgroundColor: 'inherit',
          fontFamily: 'Inter',
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          color: 'inherit',
          backgroundColor: 'inherit',
          fontFamily: 'Inter',
          ' input': {
            color: 'inherit',
            backgroundColor: 'inherit',
            fontFamily: 'Inter',
          },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: 'inherit',
          backgroundColor: 'inherit',
          fontFamily: 'Inter',
          ' input': {
            color: 'inherit',
            backgroundColor: 'inherit',
            fontFamily: 'Inter',
          },
        },
      },
    },
  },
});

const App: React.FC = () => {
  const { cur_user } = useContext(GlobalContext);
  const { isWhiteLabel } = useContext(GlobalContext);
  const isQuickcodeOnly = Boolean(cur_user?.quickcodeOnly);

  // ✅ NEW: Manage Sidebar State Here (Default = true for collapsed)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const { tenant_access } = useContext(GlobalContext);

  const resolveTenantAssetUrl = (path?: string) => {
    if (!path) return path;
    if (/^(https?:)?\/\//.test(path) || path.startsWith('/')) return path;
    return `/${path}`;
  };

  useEffect(() => {
    if (isWhiteLabel && tenant_access) {
      // Update Tab display text
      if (
        tenant_access.config?.branding &&
        tenant_access.config.branding['--client-tab-title']
      ) {
        document.title = tenant_access.config.branding['--client-tab-title'];
      } else if (tenant_access.name) {
        document.title = tenant_access.name;
      }

      // Update Tab Logo (Favicon)
      if (
        tenant_access.config &&
        (tenant_access.config.favicon || tenant_access.config.logo)
      ) {
        let link: HTMLLinkElement | null =
          document.querySelector("link[rel*='icon']");

        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }

        link.href = resolveTenantAssetUrl(
          tenant_access.config.favicon || tenant_access.config.logo
        ) as string;
      }
    }
  }, [isWhiteLabel, tenant_access]);

  if (isWhiteLabel && tenant_access && tenant_access.config) {
    applyTheme(tenant_access.config);
  }

  const excludeLayout = isWhiteLabel;

  return (
    <ThemeProvider theme={theme}>
      {!isWhiteLabel && <ProductFruitsWrapper />}
      <div
        id='MainApp'
        className={classNames('MainApp', {
          full_page: excludeLayout,
          // ✅ UPDATE: Use our state OR the quickcode check
          SideNavBar: isSidebarCollapsed || isQuickcodeOnly,
        })}
      >
        {/* ✅ UPDATE: Pass the toggle function to Header */}
        {!excludeLayout && (
          <Header
            onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          />
        )}

        <div
          className='App-Body-Layout'
          style={{ display: 'flex', width: '100%', flex: 1 }}
        >
          {!excludeLayout && !isQuickcodeOnly && (
            <div className='SideBar-block'>
              <NewSidebar />
            </div>
          )}

          <div className='right-body' style={{ flexGrow: 1, minWidth: 0 }}>
            <ScrollToTop />
            <AppActionLogger />
            {isWhiteLabel &&
              tenant_access &&
              tenant_access.config &&
              !tenant_access.config.excludeLogo &&
              tenant_access.config.logo && (
                <div className='client-logo'>
                  <div className='client-logo-block'>
                    <span>
                      {tenant_access.config.logoTitle || ''}{' '}
                      <img
                        src={resolveTenantAssetUrl(tenant_access.config.logo)}
                        alt='logo'
                      />
                    </span>
                  </div>
                </div>
              )}
            <Routes />
            {!excludeLayout && <Footer />}
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default App;
